import { motion } from "framer-motion";
import { Star, MapPin, ShieldCheck } from "lucide-react";

const cards = [
  {
    icon: Star,
    title: "Kundenbewertungen",
    text: 'heilys wird auf unabhängigen Bewertungsplattformen nach aktuellem Stand durchweg positiv bewertet: Bei Trusted Shops erreicht die Marke 4,76 von 5,0 Sternen bei über 390 Bewertungen (Urteil: „Hervorragend"). Auf Trustpilot liegt die Bewertung bei 4,3 von 5,0. Häufig gelobt werden der schnelle Versand, die sorgfältige Verpackung und die Produktqualität.',
  },
  {
    icon: MapPin,
    title: "Marke & Herkunft",
    text: "heilys ist eine Marke der weCare+ GmbH mit Sitz in Bielefeld, Deutschland. Die Rotlichtgeräte werden in Deutschland entwickelt und arbeiten mit Wellenlängen im Bereich von 630 bis 850 nm – wissenschaftlich fundierte Spektren, die in der Rotlichttherapie eingesetzt werden.",
  },
  {
    icon: ShieldCheck,
    title: "Service & Garantie",
    text: "heilys bietet nach aktuellem Stand eine 30-tägige Geld-Zurück-Garantie auf alle Produkte. Der Versand innerhalb der EU erfolgt versandkostenfrei und wird direkt aus Deutschland abgewickelt. Der Kundenservice ist per Telefon und E-Mail erreichbar.",
  },
];

const fadeUp = {
  hidden: { opacity: 0.4, y: 12 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.3 },
  }),
};

const ExperienceSection = () => (
  <section id="erfahrungen" className="py-10 md:py-20">
    <div className="container">
      <h2 className="text-center font-display text-xl font-bold text-foreground sm:text-2xl md:text-3xl">
        heilys Erfahrungen 2026 – Bewertungen &amp; Trustpilot-Score
      </h2>
      <p className="mx-auto mt-1 max-w-xl text-center text-sm font-medium text-primary/80 md:text-base">
        4,76/5 bei Trusted Shops · 4,3/5 bei Trustpilot — so bewerten Kunden heilys
      </p>
      <p className="mx-auto mt-2 max-w-2xl text-center text-sm text-muted-foreground md:mt-3 md:text-base">
        Wie schneidet heilys bei unabhängigen Bewertungsportalen ab? Die folgenden heilys Erfahrungen basieren auf öffentlich zugänglichen Daten externer Plattformen und geben einen neutralen Überblick zu Kundenerfahrungen, Herkunft und Service.
      </p>

      <div className="mt-8 md:mt-10 grid gap-4 sm:gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map((card, i) => (
          <motion.article
            key={card.title}
            custom={i}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
            className="rounded-xl border border-border/50 bg-card p-4 sm:p-5 transition-all hover:border-primary/20 hover:shadow-lg hover:shadow-primary/5"
          >
            <div className="inline-flex items-center justify-center rounded-lg bg-primary/10 p-1.5 sm:p-2">
              <card.icon className="h-4 w-4 sm:h-5 sm:w-5 text-primary" aria-hidden="true" />
            </div>
            <h3 className="mt-2 sm:mt-3 font-display text-xs sm:text-sm font-semibold text-foreground">
              {card.title}
            </h3>
            <p className="mt-1 sm:mt-1.5 text-xs sm:text-sm leading-relaxed text-muted-foreground">
              {card.text}
            </p>
          </motion.article>
        ))}
      </div>

      <div className="mx-auto mt-8 sm:mt-10 max-w-3xl space-y-3 text-sm leading-relaxed text-muted-foreground md:text-base">
        <p>
          Wer nach <strong className="text-foreground">heilys Erfahrungen</strong> sucht, findet auf den großen Bewertungsplattformen ein konsistentes Bild: Sowohl bei Trusted Shops (4,76/5) als auch bei Trustpilot (4,3/5) fallen die Kundenmeinungen überwiegend positiv aus. In den Erfahrungsberichten werden vor allem die Verarbeitungsqualität der Rotlichtgeräte, der schnelle Versand aus Deutschland und die hilfsbereite Kundenbetreuung hervorgehoben.
        </p>
        <p>
          Eine Trustpilot-Bewertung für heilys von 4,3 Sternen gilt als solides Ergebnis – auch kritischere Stimmen zur Lieferzeit oder zu einzelnen Produkten werden vom Hersteller laut öffentlichen Antworten transparent adressiert. Die Trusted-Shops-Bewertung von 4,76/5 (über 390 verifizierte Bewertungen, Stand der letzten Recherche) wird auf der Plattform mit dem Urteil „Hervorragend" geführt.
        </p>
        <p>
          Diese Übersicht zu heilys Erfahrungen 2026 basiert ausschließlich auf öffentlich zugänglichen Daten externer Bewertungsportale. heilys.info hat keine eigenen Produkttests durchgeführt und gibt die Bewertungen neutral wieder. Wer eine eigene Einschätzung treffen möchte, findet die jeweils aktuellen Erfahrungsberichte direkt bei Trustpilot und Trusted Shops.
        </p>
      </div>

      <p className="mx-auto mt-6 sm:mt-8 max-w-2xl text-center text-[11px] sm:text-xs italic text-muted-foreground">
        Hinweis: Die genannten Bewertungen stammen von externen Plattformen (Trusted Shops, Trustpilot) und geben den Stand zum Zeitpunkt der Recherche wieder. heilys.info hat keine eigenen Produkttests durchgeführt und steht in keiner direkten Verbindung zum Unternehmen heilys.
      </p>
    </div>
  </section>
);

export default ExperienceSection;
